/**
 * Polyline path renderer, extracted from Chart.tsx. Interactive: vertices are
 * drag handles, segment hit-lines insert a vertex on click, and there's a
 * per-vertex / per-path menu. It reads the chart/series/container refs at
 * event time, so the context carries the refs. Body verbatim; closed-over
 * references became ctx.*.
 */
import type { MouseEvent, MutableRefObject, RefObject, ReactNode } from "react";
import type { IChartApi } from "lightweight-charts";
import {
  type Path as PathDrawing,
  type ChartTheme,
  type SelectedDrawing,
  insertPathVertex,
  setSelectedDrawing,
  removePathVertex,
} from "@/state/slices/uiSlice";
import type { AppDispatch } from "@/state/store";
import { svgDasharrayFromName } from "./priceLineStyles";
import { drawingMenuItems } from "./drawingMenu";
import type { Candle } from "@/api/types";
import { logicalToTime, type AnySeries } from "./chartData";
import type { DragTarget, MenuItem } from "./types";
import styles from "./Chart.module.css";

export interface PathRenderCtx {
  seriesRef: MutableRefObject<AnySeries | null>;
  chartRef: MutableRefObject<IChartApi | null>;
  containerRef: RefObject<HTMLDivElement>;
  /** Needed to turn a click x into a time via the logical axis (see the
   *  segment hit-line handler) — coordinateToTime disagrees with the renderer
   *  in the empty future area. */
  candlesRef: MutableRefObject<Candle[]>;
  timeToX: (time: number) => number | null;
  selectedDrawing: SelectedDrawing | null;
  theme: ChartTheme;
  dispatch: AppDispatch;
  tabId: string;
  startDrag: (
    target: Exclude<DragTarget, { type: "trading" } | { type: "draft" }>,
  ) => (e: MouseEvent) => void;
  startLockedPan: (nativeEvent: globalThis.MouseEvent) => void;
  showMenu: (items: MenuItem[]) => (e: MouseEvent) => void;
  /** Effective chart background (chrome.bg) — used as the wave-label outline
   *  so the labels read cleanly on both the light and dark themes (a dark
   *  halo on dark, a light halo on light) instead of a fixed near-black. */
  haloColor: string;
}

export function renderPath(
  path: PathDrawing,
  opacity: number,
  key: string,
  ctx: PathRenderCtx,
): ReactNode {
  if (path.visible === false) return null;
  const series = ctx.seriesRef.current;
  if (!series) return null;

  // Convert each vertex to screen coordinates. Drop any vertex
  // whose time/price isn't currently mappable; remaining points
  // still connect into a valid polyline. (Imperative push avoids
  // TS narrowing issues with lightweight-charts' branded
  // `Coordinate` type vs our local `{x: number; y: number}`.)
  const pts: { x: number; y: number }[] = [];
  for (const pt of path.points) {
    const x = ctx.timeToX(pt.time);
    const y = series.priceToCoordinate(pt.price);
    if (x != null && y != null) {
      pts.push({ x: x as number, y: y as number });
    }
  }
  if (pts.length === 0) return null;

  const isPreview = key.startsWith("preview");
  const isSelected =
    !isPreview &&
    ctx.selectedDrawing?.kind === "path" &&
    ctx.selectedDrawing.id === path.id;
  // Per-instance overrides fall back to themed color/width/style.
  const stroke = path.color ?? ctx.theme.arrowColor ?? "#2962FF";
  const w = path.width ?? ctx.theme.arrowWidth ?? 3;
  const dash = svgDasharrayFromName(path.style ?? ctx.theme.arrowStyle);
  const pointsAttr = pts.map((p) => `${p.x},${p.y}`).join(" ");

  return (
    <g key={key} opacity={opacity}>
      {/* Visible polyline (non-interactive). markerEnd attaches the
          shared arrowhead defined on the overlay. */}
      <polyline
        points={pointsAttr}
        fill="none"
        stroke={stroke}
        strokeWidth={isSelected ? w + 1 : w}
        strokeDasharray={dash}
        markerEnd="url(#arrowhead-path)"
        pointerEvents="none"
      />

      {/* Segment hit-lines — left-click inserts a vertex at the
          click point between the two flanking vertices. Right-click
          opens the path-level context menu. Skipped in preview. */}
      {!isPreview &&
        pts.slice(0, -1).map((p1, i) => {
          const p2 = pts[i + 1];
          return (
            <line
              key={`seg-${i}`}
              x1={p1.x}
              y1={p1.y}
              x2={p2.x}
              y2={p2.y}
              stroke="transparent"
              strokeWidth={8}
              className={styles.hLineHit}
              data-drawing-hit="true"
              onMouseDown={(e) => {
                if (e.button !== 0) return;
                if (path.locked) {
                  ctx.startLockedPan(e.nativeEvent);
                  ctx.dispatch(setSelectedDrawing({ tabId: ctx.tabId, selected: { kind: "path", id: path.id } }));
                  return;
                }
                e.stopPropagation();
                const container = ctx.containerRef.current;
                const chart = ctx.chartRef.current;
                const seriesApi = ctx.seriesRef.current;
                if (!container || !chart || !seriesApi) return;
                const rect = container.getBoundingClientRect();
                const xCoord = e.clientX - rect.left;
                const yCoord = e.clientY - rect.top;
                // Logical axis → time, the exact inverse of the renderer's
                // timeToLogical. coordinateToTime walks the whitespace grid,
                // which diverges from the renderer in the empty future area
                // after a session open, so the inserted vertex would land
                // away from the click (issue #109).
                const logical = chart.timeScale().coordinateToLogical(xCoord);
                const time =
                  logical == null
                    ? null
                    : logicalToTime(logical as number, ctx.candlesRef.current);
                const price = seriesApi.coordinateToPrice(yCoord);
                if (time == null || price == null) return;
                ctx.dispatch(
                  insertPathVertex({
                    tabId: ctx.tabId,
                    id: path.id,
                    index: i + 1,
                    time: time as number,
                    price,
                  }),
                );
                ctx.dispatch(
                  setSelectedDrawing({
                    tabId: ctx.tabId,
                    selected: { kind: "path", id: path.id },
                  }),
                );
              }}
              onContextMenu={ctx.showMenu(
                drawingMenuItems(ctx.dispatch, ctx.tabId, "path", path.id),
              )}
            />
          );
        })}

      {/* Vertex circles — draggable handles + per-vertex
          right-click menu (Delete vertex / Delete path). */}
      {!isPreview &&
        pts.map((p, i) => (
          <circle
            key={`v-${i}`}
            cx={p.x}
            cy={p.y}
            r={isSelected ? 6 : 5}
            fill={isSelected ? "#3b82f6" : "#161922"}
            stroke={isSelected ? "#ffffff" : "#9aa0a6"}
            strokeWidth={1.5}
            className={styles.fibHandle}
            data-drawing-hit="true"
            onMouseDown={path.locked
              ? (e: MouseEvent) => { if (e.button !== 0) return; ctx.startLockedPan(e.nativeEvent); ctx.dispatch(setSelectedDrawing({ tabId: ctx.tabId, selected: { kind: "path", id: path.id } })); }
              : ctx.startDrag({ type: "path", id: path.id, vertexIndex: i })}
            onContextMenu={ctx.showMenu(
              drawingMenuItems(ctx.dispatch, ctx.tabId, "path", path.id, [
                {
                  label: "Delete vertex",
                  action: () =>
                    ctx.dispatch(
                      removePathVertex({ tabId: ctx.tabId, id: path.id, index: i }),
                    ),
                  danger: true,
                },
              ]),
            )}
          />
        ))}

      {/* Wave-number labels — `(1) (2) (3) …` placed up-and-right of
          each vertex. The first vertex is wave (0) and stays hidden
          (it's the implicit starting point in Elliott-Wave style);
          visible labels begin at the second vertex. Dark stroke +
          blue fill with paint-order=stroke renders the stroke first
          (as an outline behind the fill) so the text stays legible
          against busy candle backgrounds. Rendered for both
          committed paths and the in-progress preview so the user
          can count vertices as they go. */}
      {pts.map((p, i) =>
        i === 0 ? null : (
          <text
            key={`label-${i}`}
            x={p.x + 9}
            y={p.y - 9}
            fill={stroke}
            stroke={ctx.haloColor}
            strokeWidth={3}
            paintOrder="stroke"
            fontSize={12}
            fontWeight={600}
            pointerEvents="none"
          >
            ({i})
          </text>
        ),
      )}
    </g>
  );
}
